import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link2, Upload, Loader2, CheckCircle, XCircle, TrendingUp, Youtube, Music2, BarChart3, Wand2, Trash2, Send, Clock, Save, Play, Pause, Type, Mic, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminLang } from '../../hooks/useAdminLang';
import PitchStudio from '../myia/PitchStudio';

/** v50.20 — the AI agent that CREATES the videos (script + visuals + voice-over + subtitles + branded outro). */
function AiVideoCreator({ at, targets }) {
  const [topic, setTopic] = useState('weekly_cinema');
  const [subtitleStyle, setSubtitleStyle] = useState('classic');
  const [customPrompt, setCustomPrompt] = useState('');
  const [customAudioId, setCustomAudioId] = useState('');
  const [customAudioName, setCustomAudioName] = useState('');
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const audioInputRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [sched, setSched] = useState({ enabled: false, times: ['11:00', '17:00'], auto_publish: true, platforms: ['tiktok', 'youtube'], subtitle_style: 'classic' });
  const [savingSched, setSavingSched] = useState(false);
  const musicRef = useRef(null);
  const [musicPlaying, setMusicPlaying] = useState(false);

  const load = () => axios.get('/api/ai-video-creator/jobs').then(r => setJobs(r.data.jobs || [])).catch(() => {});
  const loadSched = () => axios.get('/api/ai-video-creator/scheduler').then(r => { setSched(r.data); setSubtitleStyle(r.data.subtitle_style || 'classic'); }).catch(() => {});
  useEffect(() => { load(); loadSched(); }, []);
  useEffect(() => {
    if (!jobs.some(j => j.status === 'generating')) return;
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [jobs]);

  const toggleMusicPreview = () => {
    const a = musicRef.current;
    if (!a) return;
    if (musicPlaying) { a.pause(); setMusicPlaying(false); }
    else { a.play().then(() => setMusicPlaying(true)).catch(() => toast.error(at('scpKo'))); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      await axios.post('/api/ai-video-creator/generate', {
        topic, subtitle_style: subtitleStyle,
        custom_prompt: customPrompt || '',
        custom_audio_grid_id: customAudioId || '',
      });
      toast.success(at('scvStarted'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || at('scpKo')); }
    finally { setBusy(false); }
  };

  const uploadCustomAudio = async (file) => {
    if (!file) return;
    setUploadingAudio(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post('/api/ai-video-creator/upload-audio', fd);
      setCustomAudioId(r.data.grid_id);
      setCustomAudioName(r.data.filename || file.name);
      toast.success('Audio custom ajouté');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur upload audio');
    } finally { setUploadingAudio(false); }
  };

  const publish = async (job) => {
    const plats = Object.keys(targets).filter(k => targets[k]);
    if (plats.length === 0) { toast.error(at('scvPickPlatform')); return; }
    setPublishing(job.id);
    try {
      const r = await axios.post(`/api/ai-video-creator/${job.id}/publish`, { platforms: plats }, { timeout: 900000 });
      const ok = Object.values(r.data.results || {}).every(x => x.ok);
      (ok ? toast.success : toast.warning)(ok ? at('scpPublished') : at('scpPartial'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || at('scpKo')); }
    finally { setPublishing(null); }
  };

  const remove = async (job) => {
    try { await axios.delete(`/api/ai-video-creator/${job.id}`); load(); } catch { toast.error(at('scpKo')); }
  };

  const saveSched = async () => {
    setSavingSched(true);
    try {
      const r = await axios.post('/api/ai-video-creator/scheduler', { ...sched, subtitle_style: subtitleStyle });
      setSched(r.data);
      toast.success(at('scvSchedSaved') || 'Planification enregistrée');
    } catch (e) { toast.error(e?.response?.data?.detail || at('scpKo')); }
    finally { setSavingSched(false); }
  };

  const toggleSchedPlat = (p) => setSched(s => ({
    ...s, platforms: s.platforms.includes(p) ? s.platforms.filter(x => x !== p) : [...s.platforms, p]
  }));

  const TOPICS = [
    ['weekly_cinema', at('scvTopicWeekly')],
    ['box_office', at('scvTopicBox')],
    ['news', at('scvTopicNews')],
    ['app_features', at('scvTopicApp')],
  ];

  return (
    <div className="p-3 rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/30 space-y-2" data-testid="ai-video-creator">
      <p className="text-xs font-bold text-fuchsia-300 flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" /> {at('scvTitle')}</p>
      <p className="text-[10px] text-gray-500">{at('scvSub')}</p>
      <div className="flex gap-2">
        <select value={topic} onChange={e => setTopic(e.target.value)}
          className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-xs text-white" data-testid="scv-topic-select">
          {TOPICS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
        <button onClick={generate} disabled={busy}
          className="px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:brightness-110 text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center gap-1.5"
          data-testid="scv-generate-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {at('scvGenerate')}
        </button>
      </div>

      {/* Subtitle style picker + background-music preview */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <Type className="w-3.5 h-3.5 text-fuchsia-300 shrink-0" />
          <select value={subtitleStyle} onChange={e => setSubtitleStyle(e.target.value)}
            className="flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-[11px] text-white"
            data-testid="scv-subtitle-style-select">
            <option value="classic">{at('scvSubClassic') || 'Sous-titres — Classique (blanc)'}</option>
            <option value="bold">{at('scvSubBold') || 'Sous-titres — Gras + ombre'}</option>
            <option value="yellow">{at('scvSubYellow') || 'Sous-titres — Jaune YouTube'}</option>
            <option value="cinema">{at('scvSubCinema') || 'Sous-titres — Cinéma (boîte noire)'}</option>
            <option value="neon">{at('scvSubNeon') || 'Sous-titres — Néon rose'}</option>
          </select>
        </div>
        <button onClick={toggleMusicPreview} type="button"
          className="px-3 py-1.5 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-[11px] font-semibold inline-flex items-center gap-1.5"
          data-testid="scv-music-preview-btn">
          {musicPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          {at('scvMusicPreview') || 'Écouter la musique'}
        </button>
        <audio ref={musicRef} src="/api/ai-video-creator/music-preview" preload="none" onEnded={() => setMusicPlaying(false)} />
      </div>

      {/* Custom prompt + custom audio for the AI Video Creator */}
      <textarea value={customPrompt} onChange={e => setCustomPrompt(e.target.value)} rows={2}
        placeholder={at('scvCustomPrompt') || 'Optionnel — explique à l\u2019IA ce que tu veux dans la vidéo (angle, ton, message clé)…'}
        className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-xs text-white resize-none"
        data-testid="scv-custom-prompt" />

      <div className="flex items-center gap-2">
        <input ref={audioInputRef} type="file" accept="audio/*" className="hidden"
          onChange={(e) => uploadCustomAudio(e.target.files?.[0])} data-testid="scv-custom-audio-input" />
        <button type="button" onClick={() => audioInputRef.current?.click()} disabled={uploadingAudio}
          className="flex-1 px-3 py-1.5 rounded-full bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid="scv-custom-audio-btn">
          {uploadingAudio ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mic className="w-3 h-3" />}
          {customAudioId ? (at('scvCustomAudioSet') || 'Audio custom prêt') : (at('scvCustomAudio') || 'Ajouter un audio custom (voix/musique)')}
        </button>
        {customAudioId && (
          <button onClick={() => { setCustomAudioId(''); setCustomAudioName(''); }}
            className="px-2 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px]"
            data-testid="scv-custom-audio-clear">✕</button>
        )}
      </div>
      {customAudioName && <p className="text-[10px] text-orange-300/70 -mt-1 truncate">🎵 {customAudioName}</p>}

      {/* Daily scheduler — up to 2 auto-generated + auto-published videos per day (Guyane, UTC-3) */}
      <div className="mt-2 p-2.5 rounded-xl bg-black/30 border border-fuchsia-500/20 space-y-2" data-testid="scv-scheduler">
        <label className="flex items-center gap-2 text-[11px] font-semibold text-fuchsia-200">
          <input type="checkbox" checked={sched.enabled}
            onChange={e => setSched(s => ({ ...s, enabled: e.target.checked }))}
            className="accent-fuchsia-500" data-testid="scv-sched-enabled" />
          <Clock className="w-3.5 h-3.5" /> {at('scvSchedTitle') || 'Publication auto quotidienne (2 vidéos / jour)'}
        </label>
        <div className="flex gap-2">
          {[0, 1].map(i => (
            <input key={i} type="time" value={sched.times[i] || ''}
              onChange={e => setSched(s => { const t = [...s.times]; t[i] = e.target.value; return { ...s, times: t }; })}
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-[11px] text-white"
              data-testid={`scv-sched-time-${i}`} />
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-[10px] text-gray-300">
          <input type="checkbox" checked={sched.auto_publish}
            onChange={e => setSched(s => ({ ...s, auto_publish: e.target.checked }))}
            className="accent-fuchsia-500" data-testid="scv-sched-autopub" />
          {at('scvSchedAutoPub') || 'Publier automatiquement sur les plateformes cochées'}
        </label>
        <div className="flex gap-3">
          {['tiktok', 'youtube'].map(p => (
            <label key={p} className="flex items-center gap-1 text-[10px] text-white/70">
              <input type="checkbox" checked={sched.platforms.includes(p)} onChange={() => toggleSchedPlat(p)}
                className="accent-fuchsia-500" data-testid={`scv-sched-plat-${p}`} />
              {p === 'tiktok' ? 'TikTok' : 'YouTube'}
            </label>
          ))}
        </div>
        <button onClick={saveSched} disabled={savingSched}
          className="w-full py-1.5 rounded-full bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid="scv-sched-save-btn">
          {savingSched ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {at('scvSchedSave') || 'Enregistrer la planification'}
        </button>
        <p className="text-[9px] text-gray-500 leading-snug">
          {at('scvSchedHint') || 'Heures locales (Guyane, UTC-3). Sujets alternés : cinéma / box-office.'}
        </p>
      </div>
      {jobs.length > 0 && (
        <div className="space-y-2">
          {jobs.slice(0, 4).map(j => (
            <div key={j.id} className="p-2.5 rounded-xl bg-black/30 border border-white/10" data-testid={`scv-job-${j.id}`}>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-white truncate">{j.title || at('scvGenerating')}</p>
                  <p className="text-[9px] text-gray-500">
                    {j.status === 'generating' ? `${at('scvGenerating')} (${j.progress})` : j.status === 'failed' ? `${at('avsFailed')} — ${j.error || ''}` : `${j.duration_sec || ''}s`}
                  </p>
                </div>
                {j.status === 'generating' && <Loader2 className="w-4 h-4 animate-spin text-fuchsia-300 shrink-0" />}
                {j.status === 'done' && (
                  <button onClick={() => publish(j)} disabled={publishing === j.id}
                    className="px-2.5 py-1 rounded-full bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-[10px] inline-flex items-center gap-1 shrink-0 disabled:opacity-50"
                    data-testid={`scv-publish-${j.id}`}>
                    {publishing === j.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    {at('scpPublish')}
                  </button>
                )}
                <button onClick={() => remove(j)} className="p-1.5 rounded-full hover:bg-red-500/20 text-red-400 shrink-0" data-testid={`scv-delete-${j.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {j.status === 'done' && j.video_url && (
                <video controls preload="metadata" className="mt-2 w-full max-h-72 rounded-lg bg-black" src={j.video_url} data-testid={`scv-preview-${j.id}`} />
              )}
              {j.publish_results && (
                <div className="flex gap-2 mt-1">
                  {Object.entries(j.publish_results).map(([p, r]) => (
                    <span key={p} className={`text-[9px] inline-flex items-center gap-1 ${r.ok ? 'text-emerald-300' : 'text-red-400'}`}>
                      {r.ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {p}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SocialConnectPublish() {
  const { at } = useAdminLang();
  const [status, setStatus] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [targets, setTargets] = useState({ tiktok: true, youtube: true });
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [report, setReport] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showPitch, setShowPitch] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    axios.get('/api/social-connect/status').then(r => setStatus(r.data)).catch(() => {});
    axios.get('/api/social-connect/jobs').then(r => setJobs(r.data.jobs || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const connect = async (platform) => {
    try {
      const r = await axios.get(`/api/social-connect/${platform}/start`);
      const url = r.data?.auth_url;
      if (!url) {
        toast.error(at('scpKo') || 'URL OAuth introuvable');
        return;
      }
      // Use full-tab redirect (window.open('_blank') is blocked by Safari
      // when called after an async await — the click is no longer a user gesture).
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Erreur : ${e?.message || 'inconnue'}`);
    }
  };

  const publish = async () => {
    const plats = Object.keys(targets).filter(k => targets[k]);
    if (!file || !title.trim() || plats.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      fd.append('description', desc.trim());
      fd.append('platforms', plats.join(','));
      const r = await axios.post('/api/social-connect/publish', fd, { timeout: 900000 });
      const ok = Object.values(r.data.results || {}).every(x => x.ok);
      (ok ? toast.success : toast.warning)(ok ? at('scpPublished') : at('scpPartial'));
      setFile(null); setTitle(''); setDesc('');
      if (inputRef.current) inputRef.current.value = '';
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || at('scpKo')); }
    finally { setBusy(false); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await axios.get('/api/social-connect/analytics/report', { timeout: 120000 });
      setReport(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || at('scpKo')); }
    finally { setAnalyzing(false); }
  };

  const PlatRow = ({ id, icon, label }) => {
    const st = status?.[id];
    return (
      <div className="flex items-center gap-2 p-2.5 rounded-xl bg-black/30 border border-white/10">
        {icon}
        <span className="text-xs text-white flex-1">{label}</span>
        {st?.connected ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 inline-flex items-center gap-1" data-testid={`scp-${id}-connected`}>
            <CheckCircle className="w-3 h-3" /> {at('scpConnected')}
          </span>
        ) : (
          <button onClick={() => connect(id)} className="px-3 py-1 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-[11px] inline-flex items-center gap-1" data-testid={`scp-connect-${id}`}>
            <Link2 className="w-3 h-3" /> {at('scpConnect')}
          </button>
        )}
      </div>
    );
  };

  const gain = (g) => g === null || g === undefined ? '—' : (g >= 0 ? `+${g}` : `${g}`);

  return (
    <div className="mt-4 pt-4 border-t border-sky-500/20 space-y-3" data-testid="social-connect-publish">
      <p className="text-sm font-bold text-sky-300 flex items-center gap-2"><Upload className="w-4 h-4" /> {at('scpTitle')}</p>

      {/* AI Video Creator agent */}
      <AiVideoCreator at={at} targets={targets} />

      {/* Pitch Studio — free for admins (v50.10.1) */}
      <div className="rounded-xl bg-pink-500/10 border border-pink-500/30 overflow-hidden" data-testid="admin-pitch-block">
        <button onClick={() => setShowPitch(v => !v)}
          className="w-full px-3 py-2.5 flex items-center gap-2 text-left hover:bg-white/5 transition-colors"
          data-testid="admin-pitch-toggle">
          <FileText className="w-4 h-4 text-pink-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-pink-300">{at('adminPitchTitle') || 'Studio Pitch (gratuit pour les admins)'}</p>
            <p className="text-[10px] text-gray-500">{at('adminPitchHint') || 'Téléprompteur + rédaction IA + traduction 13 langues + enregistrement vidéo/audio'}</p>
          </div>
          {showPitch ? <ChevronUp className="w-4 h-4 text-white/50" /> : <ChevronDown className="w-4 h-4 text-white/50" />}
        </button>
        {showPitch && <div className="px-2 pb-2"><PitchStudio adminFree /></div>}
      </div>

      <div className="space-y-1.5">
        <PlatRow id="tiktok" icon={<Music2 className="w-4 h-4 text-pink-400" />} label="TikTok" />
        <PlatRow id="youtube" icon={<Youtube className="w-4 h-4 text-red-500" />} label="YouTube Shorts" />
        {status && (!status.tiktok.connected || !status.youtube.connected) && (
          <p className="text-[10px] text-gray-500 leading-relaxed">
            {at('scpRedirectHint')}<br />
            TikTok: <code className="text-sky-300">{status.redirect_uris?.tiktok}</code><br />
            YouTube: <code className="text-sky-300">{status.redirect_uris?.youtube}</code>
          </p>
        )}
      </div>

      <input ref={inputRef} type="file" accept="video/*" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="scp-file-input" />
      <button onClick={() => inputRef.current?.click()} className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-sm text-white" data-testid="scp-pick-btn">
        {file ? file.name : at('scpPick')}
      </button>
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder={at('scpTitlePh')} maxLength={100}
        className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white" data-testid="scp-title-input" />
      <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder={at('scpDescPh')} rows={2} maxLength={2000}
        className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white" data-testid="scp-desc-input" />
      <div className="flex gap-3">
        {['tiktok', 'youtube'].map(p => (
          <label key={p} className="flex items-center gap-1.5 text-xs text-white/80">
            <input type="checkbox" checked={targets[p]} onChange={() => setTargets(t => ({ ...t, [p]: !t[p] }))} className="accent-sky-500" data-testid={`scp-target-${p}`} />
            {p === 'tiktok' ? 'TikTok' : 'YouTube'}
          </label>
        ))}
      </div>
      <button onClick={publish} disabled={!file || !title.trim() || busy}
        className="w-full py-2.5 rounded-full bg-gradient-to-r from-pink-500 to-red-600 hover:brightness-110 text-white font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
        data-testid="scp-publish-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {busy ? at('scpPublishing') : at('scpPublish')}
      </button>

      {jobs.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{at('scpJobs')}</p>
          {jobs.slice(0, 5).map(j => (
            <div key={j.id} className="p-2 rounded-xl bg-black/30 border border-white/10 text-[11px]" data-testid={`scp-job-${j.id}`}>
              <p className="text-white truncate">{j.title}</p>
              <div className="flex gap-2 mt-0.5">
                {Object.entries(j.results || {}).map(([p, r]) => (
                  <span key={p} className={`inline-flex items-center gap-1 ${r.ok ? 'text-emerald-300' : 'text-red-400'}`}>
                    {r.ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {p}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI analytics agent */}
      <div className="pt-3 border-t border-sky-500/20">
        <button onClick={analyze} disabled={analyzing}
          className="w-full py-2.5 rounded-full bg-gradient-to-r from-sky-500 to-indigo-600 hover:brightness-110 text-white font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
          data-testid="scp-analyze-btn">
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
          {analyzing ? at('scpAnalyzing') : at('scpAnalyze')}
        </button>
        {report && (
          <div className="mt-3 space-y-2" data-testid="scp-report">
            {Object.entries(report.platforms || {}).map(([p, d]) => (
              <div key={p} className="p-3 rounded-xl bg-black/30 border border-white/10">
                <p className="text-xs font-bold text-white capitalize flex items-center gap-1.5">
                  {p === 'tiktok' ? <Music2 className="w-3.5 h-3.5 text-pink-400" /> : <Youtube className="w-3.5 h-3.5 text-red-500" />}
                  {d.name || p}
                </p>
                <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                  <div><p className="text-lg font-bold text-white">{d.followers ?? 0}</p><p className="text-[9px] text-gray-500">{at('scpFollowers')}</p></div>
                  <div><p className={`text-lg font-bold ${(d.gain_7d ?? 0) >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>{gain(d.gain_7d)}</p><p className="text-[9px] text-gray-500">{at('scpGain7')}</p></div>
                  <div><p className={`text-lg font-bold ${(d.gain_30d ?? 0) >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>{gain(d.gain_30d)}</p><p className="text-[9px] text-gray-500">{at('scpGain30')}</p></div>
                </div>
              </div>
            ))}
            {report.ai_analysis && (
              <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/30">
                <p className="text-[10px] font-bold text-indigo-300 flex items-center gap-1 mb-1"><TrendingUp className="w-3 h-3" /> {at('scpAiTitle')}</p>
                <p className="text-[11px] text-gray-300 whitespace-pre-wrap">{report.ai_analysis}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
